/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useAreaIntroToggle } from "@/shared/ui/components/basic/useAreaIntroToggle";
import { CardListSkeleton } from "@/shared/ui/components/basic/data-state/skeletons";
import { SectionCard } from "@/features/property-management/components/PropertyDetailView/SectionCard";
import { cn } from "@/shared/ui/utils/tailwind";
import {
  getClientReferrals,
  createClientReferral,
  updateReferralStatus,
  deleteClientReferral,
  type ClientReferral,
} from "@services/requests/housien-services/ClientInsightsService";
import { ClientsService } from "@services/requests/housien-services/ClientsService";
import { findClientIdByPhone } from "../utils/resolve-referred-client";
import { ClientReferralRow } from "./ClientReferralRow";
import { ReferralAddForm } from "./ReferralAddForm";
import { Text, UnstyledButton } from '@/shared/ui';

interface Props {
  agencyId: string;
  clientId: string;
}

/**
 * Lists client indications and opens a form to register new ones.
 */
export function ClientReferralsPanel({ agencyId, clientId }: Props) {
  const { t } = useTranslation("features/clients");
  const navigate = useNavigate();
  const [referrals, setReferrals] = useState<ClientReferral[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const intro = useAreaIntroToggle({
    label: t("detail.referralsInfoLabel", "About indications"),
    body: t(
      "detail.referralsInfo",
      "People this client referred to the agency. Register name and phone to follow up and convert into a client.",
    ),
    testIdPrefix: "referrals",
  });

  const load = () => {
    getClientReferrals(agencyId, clientId)
      .then((res) => setReferrals(res))
      .catch(() => setReferrals([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [agencyId, clientId]);

  const goToClient = (id: string) => {
    navigate(`/app/agencies/${agencyId}/clients/${id}`);
  };

  /**
   * Convert an indication into a client.
   *
   * The server creates it: PATCH .../referrals/<id> with status
   * "converted" builds the client, stamps origin.channel = "referral"
   * and stores its id on the referral. This used to create one here as
   * well and send its id along -- which the route never read, so every
   * conversion produced two clients, and the one the broker was taken
   * to was the one without an origin.
   */
  const markConverted = async (referral: ClientReferral) => {
    const updated = await updateReferralStatus(
      agencyId, clientId, referral.referral_id, "converted",
    );
    const newId = updated?.converted_client_id;
    if (newId) goToClient(newId);
    else load();
  };

  const openConverted = async (referral: ClientReferral) => {
    if (referral.converted_client_id) {
      goToClient(referral.converted_client_id);
      return;
    }
    const found = await findClientIdByPhone(
      agencyId,
      referral.referred_phone,
      ClientsService.getAgencyClients,
      referral.referred_name,
    );
    if (found) {
      goToClient(found);
      return;
    }
    // Converted before the id was stored: convert again, which is a
    // no-op server-side once converted_client_id is set.
    await markConverted(referral);
  };

  const addReferral = async (payload: {
    referred_name: string;
    referred_phone: string;
  }) => {
    await createClientReferral(agencyId, clientId, payload);
    setShowForm(false);
    load();
  };

  const removeReferral = async (referral: ClientReferral) => {
    if (referral.status === "converted") return;
    await deleteClientReferral(agencyId, clientId, referral.referral_id);
    load();
  };

  if (loading) {
    return <CardListSkeleton count={2} />;
  }

  return (
    <SectionCard
      data-testid="client-referrals"
      title={t("detail.referrals", "Indications")}
      titleAccessory={intro.accessory}
    >
      {intro.panel ? <div className="mb-[var(--space-3)]">{intro.panel}</div> : null}

      {referrals.length === 0 ? (
        <Text className="text-[length:var(--text-body-sm)] text-[var(--text-tertiary)] m-0 py-[var(--space-4)] text-center">
          {t("detail.noReferrals", "No indications yet")}
        </Text>
      ) : (
        <div className="flex flex-col gap-[var(--space-3)]">
          {referrals.map((r) => (
            <ClientReferralRow
              key={r.referral_id}
              referral={r}
              convertLabel={t("detail.markConverted", "Convert")}
              statusLabel={t(`detail.referralStatus.${r.status}`, r.status)}
              removeLabel={t("detail.removeReferral", "Remove indication")}
              onConvert={() => markConverted(r)}
              onOpenConverted={() => openConverted(r)}
              onRemove={() => removeReferral(r)}
            />
          ))}
        </div>
      )}

      <div className="mt-[var(--space-3)]">
        {showForm ? (
          <ReferralAddForm onSubmit={addReferral} onCancel={() => setShowForm(false)} />
        ) : (
          <UnstyledButton
            type="button"
            onClick={() => setShowForm(true)}
            className={cn(
              "inline-flex items-center justify-center w-full h-9 px-[var(--space-3)]",
              "rounded-(--radius-md) border border-[var(--border-subtle)]",
              "text-[length:var(--text-meta)] [font-weight:var(--font-medium)]",
              "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-hover)]",
              "bg-[var(--surface-primary)] cursor-pointer transition-all duration-(--duration-fast)",
            )}
          >
            {t("detail.registerReferral", "Register indication")}
          </UnstyledButton>
        )}
      </div>
    </SectionCard>
  );
}
